// ============================================================
// HOUSE OF CARDS — endingsEngine v2 — THE BRANCHING BRAIN
// Pure logic, no React. Decides WHICH ending the player reaches.
//
// ── WHAT CHANGED IN v2 (the "one ending per tone" rework) ──
// v1 had 13 endings spread thin across 5 tones. v2 collapses to
// ONE ending per tone, six tones total, each one canonical for its
// emotional register and each one DELIBERATELY incomplete:
//
//   profound      → The New Landlady   (you inherit the post)
//   heartbreaking → What Was Saved      (NEW TONE — the brother)
//   scary         → A New Tenant        (you become the house)
//   grotesque     → Board and Lodging   (the house eats you)
//   silly         → Who's a Good House  (you adopt the house)
//   confusing     → This Was Never...   (★ THE CANON TRUE ENDING)
//
// Every ending now plants MYSTERIES — questions it raises and
// refuses to answer (see ENDINGS[].mysteries). Those questions are
// the same six threads in every ending, surfaced differently. They
// only RESOLVE in the canon Confusing ending, which is why that one
// is the true ending and the doorway to the next game (Krush).
//
// ── THE CANON / TRUE-ENDING RULE ──
// The canon ending is endWrongHouse ("This Was Never Your House").
// It is gated to Cursed Mode. Two ways to legitimately reach it:
//   (A) Replay the exact path of any ending you already completed,
//       under the curse  → state.canonViaRecreation
//   (B) Just go straight into Cursed Mode and play  → ALSO valid.
//       You do NOT need a clean/regular run first. Cursed grants it.
// Either way the player gets the true ending. We track which path
// they took for flavor + a trophy, but never gate it behind "you
// must beat regular mode first." Cursed Mode IS the qualification.
//
// ── HOW THE WIRING WORKS (unchanged from v1) ──
//  1. World choices emit { addDrive: { mercy: 2 } } via results.
//  2. App.js stores them in state.drives ({driveName: number}).
//  3. At a finale, App calls decideEnding(state) → returns the
//     ending id to run. storyScript.js owns the prose.
//
// DRIVES (the axes the house measures Tome on)
//   mercy     — kindness, tending, letting things rest
//   cruelty   — spite, defiance, breaking things
//   greed     — taking, hoarding, wanting the gold back
//   curiosity — opening what should stay shut, learning too much
//   surrender — giving the house what it wants, going quiet, going down
//   defiance  — refusing the house's terms, trying to leave/win
//
// CURSED MODE IS SACRED — we never touch the Tenant timer. We only
// READ state.cursed as an input. Cursed unlocks the canon ending and
// gives several endings a grimmer transform tail.
//
// CHARACTERS
//   Tome        — the protagonist (the "landlord"; speaker "TOME"/"YOU")
//   Mystic Ally — the woman at the porch (speaker "MYSTIC ALLY").
//                 Seems evil; Tome's read on her is unreliable — he has
//                 been steeping in a cursed place too long. She never
//                 perishes in any ending. Neither does Tome. Both persist.
//
// ADDING / EDITING AN ENDING
//  1. Edit the entry in ENDINGS (id, tone, requires, mysteries...).
//  2. Edit the matching script block of the same id in storyScript.js.
//  No App.js changes needed for ending content.
// ============================================================

// ── DRIVE NAMES (single source of truth) ─────────────────────
export const DRIVES = ["mercy", "cruelty", "greed", "curiosity", "surrender", "defiance"];

export function emptyDrives() {
  return DRIVES.reduce((o, d) => ((o[d] = 0), o), {});
}

// Merge an { addDrive: {...} } result fragment into a drives map.
// Returns a NEW object (App applies it immutably).
export function applyDrives(drives, add) {
  if (!add) return drives;
  const next = { ...emptyDrives(), ...drives };
  for (const k of Object.keys(add)) next[k] = (next[k] || 0) + add[k];
  return next;
}

export function topDrive(drives) {
  const d = { ...emptyDrives(), ...drives };
  let best = "surrender";
  let bestN = -Infinity;
  for (const k of DRIVES) {
    if (d[k] > bestN) {
      bestN = d[k];
      best = k;
    }
  }
  return { drive: best, score: bestN };
}

// True if every flag in `need` is set truthy on state.flags.
const hasFlags = (state, need = []) => need.every((f) => state.flags[f]);
// True if NONE of the flags in `avoid` are set.
const lacksFlags = (state, avoid = []) => avoid.every((f) => !state.flags[f]);

// ── THE SIX CANON MYSTERY THREADS ────────────────────────────
// These are the unanswered questions every ending raises and only the
// canon ending resolves. Each ending's `mysteries` array lists which
// threads it leaves dangling (surfaced in that ending's own voice).
// Keeping them centralized means a future "loose ends" / journal
// screen can show the player exactly which threads they've seen.
export const MYSTERY_THREADS = {
  voiceBelow:   "Whose voice calls Tome's name from below the stairs?",
  theBrother:   "Was the brother ever really down there — or only the shape of him?",
  mysticAlly:   "What is Mystic Ally, and why does she never leave the porch — or perish?",
  theGround:    "What was burned into this ground before the house, and why?",
  thePost:      "Who keeps the house when the keeper is gone? Has it always been Tome?",
  tomesShadow:  "Why does Tome feel STRONGER the deeper he goes, when he should feel weaker?",
};
export const ALL_MYSTERY_IDS = Object.keys(MYSTERY_THREADS);

// ── THE ENDINGS LEDGER (v2 — one per tone) ───────────────────
// tone drives the credits screen; logic uses `requires` + priority.
// Higher priority wins when several qualify. The toll ending is the
// LOW-priority default so the tone routes pre-empt it.
//
// requires spec fields (all optional, all ANDed):
//   cursed:    true|false        — must match state.cursed
//   flags:     [..]              — all must be set
//   notFlags:  [..]              — none may be set
//   drive:     "mercy"           — must be the top drive
//   minDrive:  { mercy: 3 }      — each listed drive >= n
//   maxDrive:  { cruelty: 0 }    — each listed drive <= n
//   test:      (state, ctx)=>bool — escape hatch for anything custom
//
// New fields:
//   canon:     true              — this is THE true ending
//   mysteries: [..]              — MYSTERY_THREADS ids left open
export const ENDINGS = [
  // ───────────────────────── PROFOUND ─────────────────────────
  {
    id: "endLandlord",
    title: "The New Landlady",
    tone: "profound",
    priority: 80,
    // You understood the house as a job, not a monster. You don't beat
    // it or feed it — you INHERIT it. Curiosity + surrender, no cruelty.
    requires: {
      minDrive: { curiosity: 3, surrender: 2 },
      maxDrive: { cruelty: 1 },
    },
    mysteries: ["thePost", "mysticAlly", "tomesShadow"],
  },

  // ───────────────────── HEARTBREAKING (NEW) ──────────────────
  {
    id: "endReunion",
    title: "What Was Saved",
    tone: "heartbreaking",
    priority: 95,
    // The whole point of the locket/brother thread. Tome went down for
    // the right reasons, carrying the right thing. The grief is the point.
    requires: {
      flags: ["egg_what_was_saved", "egg_one_more_story"],
      minDrive: { mercy: 4 },
      maxDrive: { greed: 1 },
    },
    mysteries: ["theBrother", "voiceBelow", "tomesShadow"],
  },

  // ───────────────────────── SCARY ────────────────────────────
  {
    id: "endTenant",
    title: "A New Tenant",
    tone: "scary",
    priority: 90,
    // Cursed-only. You surrendered utterly while it hunted you. You
    // don't die — you take the Tenant's place. Worse than death.
    requires: {
      cursed: true,
      minDrive: { surrender: 4 },
    },
    mysteries: ["thePost", "voiceBelow", "tomesShadow"],
  },

  // ─────────────────────── GROTESQUE ──────────────────────────
  {
    id: "endDigested",
    title: "Board and Lodging",
    tone: "grotesque",
    priority: 88,
    // You fed it everything, took everything, and it includes you in
    // the inventory. Greed + surrender. Wet ending.
    requires: {
      minDrive: { greed: 3, surrender: 2 },
    },
    mysteries: ["theGround", "voiceBelow", "tomesShadow"],
  },

  // ──────────────────────── SILLY ─────────────────────────────
  {
    id: "endGoodBoy",
    title: "Who's a Good House",
    tone: "silly",
    priority: 86,
    // You played Fetch, bounced the ball, treated the nightmare like a
    // big weird dog. It works. It just works.
    requires: {
      flags: ["egg_fetch", "egg_nothin_but_net"],
      minDrive: { mercy: 1 },
      maxDrive: { cruelty: 1 },
    },
    mysteries: ["mysticAlly", "thePost", "tomesShadow"],
  },

  // ─────────────────── CONFUSING ★ CANON TRUE ─────────────────
  {
    id: "endWrongHouse",
    title: "This Was Never Your House",
    tone: "confusing",
    priority: 96, // high but doesn't auto-win every cursed run
    canon: true,
    // ★ THE TRUE ENDING. Cursed-gated. Reached by playing through
    // Cursed Mode and meeting one of these conditions:
    //   (A) The cursed_unraveled flag (set when enough cursed content
    //       resolves its threads — wired as cursed endings are authored)
    //   (B) canonViaRecreation: completed a base ending, now replaying
    //       under the curse (detected by App.js at decideEnding time)
    // Regular mode NEVER routes here. Cursed mode routes here only when
    // the player has earned it, not automatically.
    requires: {
      cursed: true,
      test: (state) =>
        state.canonViaRecreation === true ||
        !!state.flags.cursed_unraveled,
    },
    mysteries: [], // resolves them all instead of leaving them open
  },

  // ════════════════ CURSED MODE ENDINGS (one per tone) ════════
  // Tailor-made for Cursed Mode. NOT reskins of the base endings —
  // these are new stories that deepen the mystery threads and lead
  // toward the canon true ending. Each one answers some threads the
  // base endings left open, while planting darker ones.
  //
  // TODO: author prose in storyScript.js for each. The entries below
  // are the routing rules; the script blocks are placeholders until
  // the tailor-made content is written.
  {
    id: "endCursedProfound",
    title: "The Post Never Ends",
    tone: "profound",
    priority: 81, // beats base endLandlord (80) when cursed
    requires: {
      cursed: true,
      minDrive: { curiosity: 3, surrender: 2 },
      maxDrive: { cruelty: 1 },
    },
    mysteries: ["tomesShadow", "thePost"],
  },
  {
    id: "endCursedHeartbreaking",
    title: "The Shape in the Crib",
    tone: "heartbreaking",
    priority: 96, // beats base endReunion (95) when cursed
    requires: {
      cursed: true,
      flags: ["egg_what_was_saved", "egg_one_more_story"],
      minDrive: { mercy: 4 },
      maxDrive: { greed: 1 },
    },
    mysteries: ["theBrother", "tomesShadow"],
  },
  {
    id: "endCursedScary",
    title: "Every Tenant Before You",
    tone: "scary",
    priority: 91, // beats base endTenant (90)
    requires: {
      cursed: true,
      minDrive: { surrender: 4 },
    },
    mysteries: ["voiceBelow", "thePost"],
  },
  {
    id: "endCursedGrotesque",
    title: "What the Ground Remembers",
    tone: "grotesque",
    priority: 89, // beats base endDigested (88)
    requires: {
      cursed: true,
      minDrive: { greed: 3, surrender: 2 },
    },
    mysteries: ["theGround", "tomesShadow"],
  },
  {
    id: "endCursedSilly",
    title: "Fetch, Forever",
    tone: "silly",
    priority: 87, // beats base endGoodBoy (86)
    requires: {
      cursed: true,
      flags: ["egg_fetch", "egg_nothin_but_net"],
      minDrive: { mercy: 1 },
      maxDrive: { cruelty: 1 },
    },
    mysteries: ["mysticAlly", "tomesShadow"],
  },

  // ───────────────────── LOW-PRIORITY DEFAULTS ────────────────
  // Kept so a finale ALWAYS resolves to something coherent even if the
  // player's drives/flags don't hit a tone route. Not "tone endings" —
  // they're the floor. Both still plant mysteries.
  {
    id: "endTeeth",
    title: "Six Little Teeth",
    tone: "scary",
    priority: 10, // toll-paid default (the original Chapter One toll)
    requires: { flags: ["toll_paid"] },
    mysteries: ["voiceBelow", "theBrother", "theGround"],
  },
  {
    id: "endWalkedOut",
    title: "Walked Out",
    tone: "confusing",
    priority: 8, // no-toll default — below everything, above nothing
    requires: { notFlags: ["toll_paid"] },
    mysteries: ["voiceBelow", "mysticAlly", "thePost"],
  },
];

// ── CURSED-ONLY DARK TAILS ───────────────────────────────────
// v1 had CURSED_TRANSFORMS: base endings got a grimmer script swap
// under the curse. v2 REMOVES that system. Cursed Mode gets its own
// TAILOR-MADE endings (one per tone, separate entries in ENDINGS).
// They are NOT reskins of the base endings — they're new stories that
// dovetail with the base endings and deepen the mystery threads.
//
// The old transform map is gone. Each cursed ending is its own
// ENDINGS entry with `requires: { cursed: true, ... }`.
// TODO: author the 6 cursed endings (see placeholder entries above).
export const CURSED_TRANSFORMS = {}; // kept as empty for API compat

// ── THE DECISION ─────────────────────────────────────────────
// Returns { endingId, scriptId, meta } — App runs scriptId.
//
// IMPORTANT: Cursed Mode has its own tailor-made endings (endCursedX)
// that route via their own requirements, not auto-winning anything.
// The canon true ending (endWrongHouse) requires cursed_unraveled flag
// OR canonViaRecreation, so cursed playthroughs start at the base
// endings and earn the canon over time.
export function decideEnding(state) {
  const drives = { ...emptyDrives(), ...(state.drives || {}) };
  const ctx = { drives, top: topDrive(drives) };

  const qualifies = (e) => {
    const r = e.requires || {};
    if (typeof r === "function") return r(state, ctx);
    if (r.cursed === true && !state.cursed) return false;
    if (r.cursed === false && state.cursed) return false;
    if (r.flags && !hasFlags(state, r.flags)) return false;
    if (r.notFlags && !lacksFlags(state, r.notFlags)) return false;
    if (r.drive && ctx.top.drive !== r.drive) return false;
    if (r.minDrive) for (const k in r.minDrive) if ((drives[k] || 0) < r.minDrive[k]) return false;
    if (r.maxDrive) for (const k in r.maxDrive) if ((drives[k] || 0) > r.maxDrive[k]) return false;
    if (r.test && !r.test(state, ctx)) return false;
    return true;
  };

  const matches = ENDINGS.filter(qualifies).sort((a, b) => b.priority - a.priority);
  // Fallbacks never contradict the player's last action.
  const fallbackId = state.flags.toll_paid ? "endTeeth" : "endWalkedOut";
  const winner = matches[0] || ENDINGS.find((e) => e.id === fallbackId);

  let scriptId = winner.id;
  if (state.cursed && CURSED_TRANSFORMS[winner.id]) {
    scriptId = CURSED_TRANSFORMS[winner.id];
  }

  return {
    endingId: winner.id,
    scriptId,
    meta: {
      tone: winner.tone,
      title: winner.title,
      canon: !!winner.canon,
      mysteries: winner.mysteries || [],
      topDrive: ctx.top.drive,
      drives,
      considered: matches.map((m) => m.id),
    },
  };
}

// ── CANON COMPLETION TRACKING ────────────────────────────────
// Call after an ending resolves to update the player's record.
// `state.completedEndings` is a map { endingId: true }.
// `state.canonDone` flips true once the canon true ending is reached.
// Returns a NEW patch object for App to merge into state immutably.
export function recordEnding(state, endingId) {
  const completed = { ...(state.completedEndings || {}), [endingId]: true };
  const isCanon = !!(endingById(endingId) && endingById(endingId).canon);
  return {
    completedEndings: completed,
    canonDone: state.canonDone || isCanon,
  };
}

// Has the player completed a given (non-canon) ending they could now
// recreate under the curse? Used to set state.canonViaRecreation when
// a cursed replay retraces a known ending path. (Path A above.)
export function hasCompletedAnyEnding(state) {
  const c = state.completedEndings || {};
  return Object.keys(c).some((id) => id !== "endWrongHouse" && c[id]);
}

// ── ENDING REGISTRY HELPERS (for a future "endings discovered" screen) ──
export function endingById(id) {
  return ENDINGS.find((e) => e.id === id) || null;
}
export const ALL_ENDING_IDS = ENDINGS.map((e) => e.id);
export const ENDINGS_BY_TONE = ENDINGS.reduce((o, e) => {
  (o[e.tone] = o[e.tone] || []).push(e.id);
  return o;
}, {});

// The six canonical tones, in presentation order. Useful for a
// "tones discovered: 4/6" style completion readout.
export const TONES = ["profound", "heartbreaking", "scary", "grotesque", "silly", "confusing"];
